import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { subscribeToast } from '../../lib/toast';
import { EASE_OUT_IOS, SPRING_POP, useReducedMotion } from '../../lib/motion';

interface ToastItem {
  id: number;
  message: string;
}

/**
 * Global toast host — mounted once in main.tsx. Renders transient confirmations
 * (e.g. "Link copied") pushed through the toast bus. Each toast springs up into
 * place and animates back out (never a hard cut); stacked toasts reflow smoothly
 * as others leave. Sits above the tab bar. Honours Reduce Motion.
 */
export function ToastHost() {
  const [items, setItems] = useState<ToastItem[]>([]);
  const reduce = useReducedMotion();

  useEffect(() => {
    let seq = 0;
    return subscribeToast((message) => {
      const id = ++seq;
      setItems((cur) => [...cur, { id, message }]);
      window.setTimeout(() => setItems((cur) => cur.filter((i) => i.id !== id)), 2400);
    });
  }, []);

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-[calc(env(safe-area-inset-bottom)+84px)] z-[var(--z-toast)] flex flex-col items-center gap-2 px-4">
      <AnimatePresence initial={false}>
        {items.map((i) => (
          <motion.div
            key={i.id}
            layout
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 16, scale: 0.9 }}
            animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: 8, scale: 0.95 }}
            transition={reduce ? { duration: 0.18, ease: EASE_OUT_IOS } : SPRING_POP}
            className="pointer-events-auto rounded-full bg-night px-4 py-2.5 text-center text-[13px] font-bold text-white shadow-[0_14px_34px_-10px_rgba(0,0,0,0.6)]"
          >
            {i.message}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
